import { EmbeddedContentDefinition } from 'modules/graphql';
import { MediaLayout } from 'modules/placements/types';
import { TemplateEditViews } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { usePreviewWithDataContext } from 'modules/templateEditor/context/PreviewWithDataContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { Logo, LogoColors } from 'ui/logo';
import { Platform } from '../constants';
import { useEmbeddedPreviewContext } from '../context/EmbeddedPreviewContext';
import { MediaLayoutProp } from '../types';
import {
  ActionButtonText,
  ActionButtonsContainer,
  Body,
  Media,
  MessageContainer,
  MessageContent,
  MessageText,
  StyledTab,
  Title,
} from './styles';

type Props = {
  platform?: Platform;
  content?: EmbeddedContentDefinition;
} & MediaLayoutProp;

export const Message = ({ platform = Platform.MOBILE, mediaLayout, content }: Props) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { view } = useQueryParamContext();
  const { location } = useHistory();
  const { isDataFeedDataEmpty, isUserDataEmpty } = usePreviewWithDataContext();
  const {
    title: previewTitle,
    body: previewBody,
    mediaUrl: previewMediaUrl,
    actionButtons: previewActionButtons,
  } = useEmbeddedPreviewContext();

  // Placements uses this preview rendering with the preview context values
  const isPlacementsView = location?.pathname?.includes('placements');
  const usePopulatedField =
    (isPlacementsView ||
      (view === TemplateEditViews.preview && (!isDataFeedDataEmpty || !isUserDataEmpty))) &&
    !content;

  // content takes precedence over liveTemplate
  const embeddedData = (content || liveTemplate?.mediumData || {}) as EmbeddedContentDefinition;
  const renderedTitle = usePopulatedField ? previewTitle : embeddedData.title;
  const renderedBody = usePopulatedField ? previewBody : embeddedData.body;
  const renderedMediaUrl = usePopulatedField ? previewMediaUrl : embeddedData.elements?.mediaUrl;
  const renderedActionButtons = usePopulatedField
    ? previewActionButtons
    : embeddedData.elements?.buttons;

  const [isValidMedia, setIsValidMedia] = useState<boolean>(false);
  const validMediaSrc = isValidMedia && !!renderedMediaUrl;

  return (
    <MessageContainer
      data-test="embedded-message-preview"
      id="embedded-message-preview"
      mediaLayout={mediaLayout}
      platform={platform}
    >
      {mediaLayout !== MediaLayout.NONE && (
        <Media
          id="embedded-message-preview-media"
          isVisible={validMediaSrc}
          mediaLayout={mediaLayout}
          platform={platform}
        >
          <img
            src={renderedMediaUrl}
            onLoad={() => setIsValidMedia(true)}
            onError={() => setIsValidMedia(false)}
          />
          {!validMediaSrc && (
            <Logo
              height={mediaLayout === MediaLayout.RIGHT || platform === Platform.DESKTOP ? 24 : 36}
              color={LogoColors.BLACK}
            />
          )}
        </Media>
      )}
      <MessageContent mediaLayout={mediaLayout} platform={platform}>
        <MessageText mediaLayout={mediaLayout}>
          <Title id="embedded-message-preview-title">{renderedTitle}</Title>
          <Body id="embedded-message-preview-body">{renderedBody}</Body>
        </MessageText>
        {!!renderedActionButtons?.length && (
          <ActionButtonsContainer
            id="embedded-message-preview-action-buttons"
            data-test="embedded-message-preview-action-buttons"
            mediaLayout={mediaLayout}
            platform={platform}
          >
            {renderedActionButtons.map((button, index) => {
              const key = `preview-action-button-${index}`;
              if (mediaLayout !== MediaLayout.TOP && index === 0)
                return <StyledTab key={key} id={key} label={button.title} />;
              return <ActionButtonText key={key}>{button.title}</ActionButtonText>;
            })}
          </ActionButtonsContainer>
        )}
      </MessageContent>
    </MessageContainer>
  );
};
